import { useEffect, useState, useRef } from "react";
import Select from "react-select";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import apiInstance from "../../utils/axios";
import axiosPublic from "../../utils/axios_public";
import formatIngredients from "../../utils/formatIngredients";
import UserData from "../plugins/UserData";
import '../../../css/productDetails.css';
import { Link, useNavigate } from "react-router-dom";
import { useAuthApp } from '../../app/auth';
import { useTranslation } from 'react-i18next';

const Toast = Swal.mixin({
    toast: true,
    position: "top",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
});

function ProductDetails() {
    const [isLoading, setIsLoading] = useState(false);
    const [productId, setProductId] = useState('')
    const [productDetails, setProductDetails] = useState([]);
    const [productNutrients, setProductNutrients] = useState([]);
    const [productIngredients, setProductIngrediens] = useState([]);
    const [formattedIngredients, setFormattedIngredients] = useState("");
    const { t } = useTranslation();
    const item2Ref = useRef(null); // Reference for grid-item-2



    let lang_browser = navigator.language.toLowerCase();
    let lang = localStorage.getItem('language') || (lang_browser.startsWith("en") ? "en" : lang_browser);


    const {hashedId} = useParams()

    const getId = async (hashedId) => {
        const decodeResponse = await axiosPublic.get(`decode-id/${hashedId}/`);
        setProductId(decodeResponse.data.id);
    }


    const getProductDetails = async () => {
        try {
            const response = await axiosPublic.get(`/products/product_detail/?product=${productId}`, {headers: { "Accept-Language": `${lang}` }});  //`products/${hashedId}/` /api/v1/products/Oqgw/product_detail/
            if (response.data) {
                setProductDetails(response.data)
            }
            
        } catch (error) {
            console.error("Error fetching product details:", error);
            throw error; // Rethrow the error to be handled by the caller
        }
    };

    const getProductNutrients = async () => {
        try {
            const response = await axiosPublic.get(`/product_nutrients/?product=${productId}`)
            
            if (response.data) {
                setProductNutrients(response.data[0])
            }
        } catch (error) {
            console.error("Error fetching product nutrients:", error);
            throw error; 
        }
        
    }

    const getProductIngredients = async () => {
        try {
            const response = await axiosPublic.get(`/product-ingredients-detail/product_ingredients/?product=${productId}`,{headers: { "Accept-Language": `${lang}` }})
            if (response.data) {
                setProductIngrediens(response.data)
            }            
        } catch (error) {
            console.error("Error fetching product ingredients:", error);
            throw error; 
        }
    }

    const fetchData = async () => {
        await Promise.all([getProductDetails(), getProductNutrients(), getProductIngredients()]);
    }

    useEffect(() => {
        setIsLoading(true);
        getId(hashedId)
        if (productId) {
            fetchData();
        }
        if (productIngredients) {
            setFormattedIngredients(formatIngredients(productIngredients));
        }
    },[productId, lang])

    useEffect(() => {
        if (productIngredients) {
            setFormattedIngredients(formatIngredients(productIngredients));
        }
        
    },[productIngredients, lang])

    useEffect(() => {
        if (productDetails.product_picture && window.innerWidth < 1200) {
            // Scroll to grid-item-2 after 2 seconds
            const timer = setTimeout(() => {
                if (item2Ref.current) {
                    item2Ref.current.scrollIntoView({
                        behavior: "smooth", // Smooth scrolling
                        block: "start", // Align the top of the element with the top of the viewport
                    });

                }
            }, 2000);
    
            return () => clearTimeout(timer); // Cleanup timeout on component unmount
        }
    }, [productDetails]);


  return (
    <>
        <div className="">
            <div className="product-details-header">
                <h1>{t("productDetails.headings.winery")} {productDetails.winery}</h1>
                <h2>{productDetails.product_name} <i>{productDetails.sub_category}</i></h2>
            </div>
            
            {formattedIngredients && (
                <div className="grid-container-2">
                <div className={productDetails && productDetails.product_picture ? "grid-item-2" : "grid-item-2 is-hidden"} key='item-1' >
                    <img className="wine-bottle-pic-det" src={productDetails.product_picture} />
                </div>
                <div className="grid-item-2" key='item-2' ref={item2Ref}>
                    <dl className="definition-list">
                        <div className="definition-item">
                            <dt>{t("productDetails.labels.wineType")}</dt>
                            <dd>
                                {productDetails.wine_type?.length > 0 ? productDetails?.wine_type.join(', ') : productDetails.wine_type}
                            </dd>
                        </div>
                    </dl>
                    <dl className="definition-list">
                        <div className="definition-item">
                            <dt>{t("productDetails.labels.origin")}</dt>
                            <dd>
                                {productDetails.origin}
                            </dd>
                        </div>
                    </dl>
                    <dl className="definition-list">
                        <div className="definition-item">
                            <dt>{t("productDetails.labels.wineQuality")}</dt>
                            <dd>
                                {productDetails.wine_qual}
                            </dd>
                        </div>
                    </dl>
                    <dl className="definition-list">
                        <div className="definition-item">
                            <dt>{t("productDetails.labels.vintage")}</dt>
                            <dd>
                                {productDetails.vintage}
                            </dd>
                        </div>
                    </dl>
                    <dl className="definition-list">
                        <div className="definition-item">
                            <dt>{t("productDetails.labels.style")}</dt>
                            <dd>
                                {productDetails.sub_category}
                            </dd>
                        </div>
                    </dl>
                    <dl className="definition-list">
                        <div className="definition-item">
                            <dt>{t("productDetails.labels.blending")}</dt>
                            <dd>
                                {productDetails.blending_type || '-'}
                            </dd>
                        </div>
                    </dl>
                    <dl className="definition-list">
                        <div className="definition-item">
                            <dt>{t("productDetails.labels.totalAcid")}</dt>
                            <dd>
                                {productNutrients.total_acid} g/L
                            </dd>
                        </div>
                    </dl>
                    <dl className="definition-list">
                        <div className="definition-item">
                            <dt>{t("productDetails.labels.totalSugar")}</dt>
                            <dd>
                                {productNutrients.total_sugar} g/L
                            </dd>
                        </div>
                    </dl>
                    <dl className="definition-list">
                        <div className="definition-item">
                            <dt>{t("productDetails.labels.alcohol")}</dt>
                            <dd>
                                {productNutrients.alcohol} %vol.
                            </dd>
                        </div>
                    </dl>
                    <dl>
                        <div className="definition-list-zutaten">
                            <dt>{t("productDetails.headings.ingredients")}</dt>
                                <dd>
                                    <p dangerouslySetInnerHTML={{ __html: formattedIngredients }} />
                                </dd>
                        </div>
                    </dl>
                    <table className="nutrient-table">
                        <thead>
                            <tr>
                                <th>{t("productDetails.nutrientsTable.title")}</th>
                                <th>{t("productDetails.nutrientsTable.headers.averageContent")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>{t("productDetails.nutrientsTable.rows.caloricValue")}</td>
                                <td>{productNutrients.caloric_value_kj} kJ / {productNutrients.caloric_value_kcal} kcal</td>
                            </tr>
                            <tr>
                                <td>{t("productDetails.nutrientsTable.rows.carbohydrates")}</td>
                                <td>{parseInt(productNutrients.glycerin) + parseInt(productNutrients.total_sugar)} g</td>
                            </tr>

                            <tr>
                                <td className="shifted"><i>{t("productDetails.nutrientsTable.rows.ofWhichSugar")}</i></td>
                                <td>{productNutrients.total_sugar} g</td>
                            </tr>

                            {productNutrients.specific_data_FSPS ?
                                <>
                                    <tr>
                                        <td>{t("productDetails.nutrientsTable.rows.fat")}</td>
                                        <td>{productNutrients.fat} g</td>
                                    </tr>
                                    <tr>
                                        <td>{t("productDetails.nutrientsTable.rows.saturatedFattyAcids")}</td>
                                        <td>{productNutrients.saturated_fatty_acids} g</td>
                                    </tr>
                                    <tr>
                                        <td>{t("productDetails.nutrientsTable.rows.proteins")}</td>
                                        <td>{productNutrients.proteins} g</td>
                                    </tr>
                                    <tr>
                                        <td>{t("productDetails.nutrientsTable.rows.salts")}</td>
                                        <td>{productNutrients.salts} g</td>
                                    </tr>
                                </>
                                :
                                <tr>
                                    <td></td>
                                    <td>{t("productDetails.nutrientsTable.rows.minorAmounts")}</td>
                                </tr>
                        }
                        </tbody>
                    </table>
                </div>
            </div>
            )}
        </div>
    </>
  )
}

export default ProductDetails